import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react'
import { useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { NavLink } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'
import { useEscapeKey } from '@/hooks/useDismiss'
import { useFocusTrap, useScrollLock } from '@/hooks/useFocusTrap'
import { useT } from '@/i18n'
import { MODULES_BY_GROUP } from '@/config/modules'
import { cn } from '@/lib/utils'

import { Wordmark } from './Wordmark'

type NavContentProps = {
  collapsed: boolean
  onNavigate?: () => void
}

function NavContent({ collapsed, onNavigate }: NavContentProps): ReactNode {
  const t = useT()

  return (
    <nav aria-label={t('nav.primary')} className="scrollbar-thin flex-1 overflow-y-auto px-2 py-3">
      {MODULES_BY_GROUP.map(({ group, modules }) => (
        <div key={group} className="mb-4 last:mb-0">
          {collapsed ? (
            <div className="mx-2 mb-2 h-px bg-border" role="presentation" />
          ) : (
            <h2 className="px-2 pb-1.5 text-overline uppercase text-muted-foreground">
              {t(`nav.groups.${group}`)}
            </h2>
          )}

          <ul className="space-y-0.5">
            {modules.map((module) => {
              const Icon = module.icon
              const label = t(`modules.${module.id}.title`)

              const link = (
                <NavLink
                  to={module.path}
                  end={module.path === '/'}
                  onClick={onNavigate}
                  aria-label={collapsed ? label : undefined}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-2.5 rounded-md px-2 py-1.5',
                      'text-body-sm transition-colors duration-hover',
                      collapsed && 'justify-center px-0',
                      isActive
                        ? 'bg-primary/10 font-medium text-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-primary transition-opacity duration-hover',
                          isActive ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <Icon aria-hidden="true" className="size-4 shrink-0" />
                      {collapsed ? null : <span className="truncate">{label}</span>}
                    </>
                  )}
                </NavLink>
              )

              return (
                <li key={module.id}>
                  {collapsed ? (
                    <Tooltip content={label} side="right" triggerClassName="w-full">
                      {link}
                    </Tooltip>
                  ) : (
                    link
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </nav>
  )
}

export type SidebarProps = {
  collapsed: boolean
  onToggleCollapsed: () => void
}

/** Desktop rail. Hidden below `md`, where `MobileNav` takes over. */
export function Sidebar({ collapsed, onToggleCollapsed }: SidebarProps): ReactNode {
  const t = useT()

  return (
    <aside
      className={cn(
        'sticky top-0 hidden h-svh shrink-0 flex-col border-r border-border bg-card md:flex',
        'transition-[width] duration-modal ease-inOut',
        collapsed ? 'w-14' : 'w-60',
      )}
    >
      <div
        className={cn(
          'flex h-14 shrink-0 items-center border-b border-border px-2',
          collapsed ? 'justify-center' : 'justify-between pl-3',
        )}
      >
        <Wordmark compact={collapsed} />
      </div>

      <NavContent collapsed={collapsed} />

      <div className={cn('shrink-0 border-t border-border p-2', collapsed && 'flex justify-center')}>
        <Button
          variant="ghost"
          size={collapsed ? 'iconSm' : 'sm'}
          onClick={onToggleCollapsed}
          aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
          aria-expanded={!collapsed}
          className={cn(!collapsed && 'w-full justify-start')}
          icon={
            collapsed ? (
              <PanelLeftOpen aria-hidden="true" />
            ) : (
              <PanelLeftClose aria-hidden="true" />
            )
          }
        >
          {collapsed ? null : t('nav.collapse')}
        </Button>
      </div>
    </aside>
  )
}

export type MobileNavProps = {
  open: boolean
  onClose: () => void
}

/** Below `md` the sidebar becomes a modal overlay drawer. */
export function MobileNav({ open, onClose }: MobileNavProps): ReactNode {
  const t = useT()
  const panelRef = useRef<HTMLDivElement>(null)

  useEscapeKey(open, onClose)
  useFocusTrap(panelRef, open)
  useScrollLock(open)

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50 md:hidden">
      <div
        className="animate-overlay-in absolute inset-0 bg-neutral-950/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.primary')}
        tabIndex={-1}
        className="animate-drawer-in-left absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-card shadow-3 outline-none"
      >
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-border pl-3 pr-2">
          <Wordmark compact={false} />
          <Button variant="ghost" size="iconSm" onClick={onClose} aria-label={t('nav.closeNavigation')}>
            <X aria-hidden="true" />
          </Button>
        </div>
        <NavContent collapsed={false} onNavigate={onClose} />
      </div>
    </div>,
    document.body,
  )
}
